import { type ChangeEvent, type ReactElement, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface PriceRangeSliderProps {
    minAllowed: number;
    maxAllowed: number;
    minValue: number;
    maxValue: number;
    minLabel: string;
    maxLabel: string;
    onChange: (minValue: number, maxValue: number) => void;
    onCommit: (minValue: number, maxValue: number) => void;
    className?: string;
}

const THUMB_SIZE = '1rem'; // matches h-4 / w-4 thumbs
const THUMB_RADIUS = '0.5rem'; // half thumb — track inset so ends align with thumb centers

const rangeInputClassName = cn(
    'absolute inset-x-0 top-1/2 -translate-y-1/2 m-0 h-4 w-full cursor-pointer appearance-none bg-transparent pointer-events-none',
    '[&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:bg-transparent',
    '[&::-moz-range-track]:h-1 [&::-moz-range-track]:bg-transparent',
    '[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:z-20',
    '[&::-webkit-slider-thumb]:-mt-1.5 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4',
    '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full',
    '[&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-foreground',
    '[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:z-20',
    '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:appearance-none',
    '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-foreground',
    'focus-visible:outline-none focus-visible:[&::-webkit-slider-thumb]:ring-2 focus-visible:[&::-webkit-slider-thumb]:ring-ring',
    'focus-visible:[&::-moz-range-thumb]:ring-2 focus-visible:[&::-moz-range-thumb]:ring-ring'
);

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function toPercent(value: number, min: number, max: number): number {
    if (max <= min) return 0;
    return ((value - min) / (max - min)) * 100;
}

export default function PriceRangeSlider({
    minAllowed,
    maxAllowed,
    minValue,
    maxValue,
    minLabel,
    maxLabel,
    onChange,
    onCommit,
    className,
}: PriceRangeSliderProps): ReactElement {
    const latestValuesRef = useRef({ min: minValue, max: maxValue });

    useEffect(() => {
        latestValuesRef.current = { min: minValue, max: maxValue };
    }, [minValue, maxValue]);

    const minPercent = toPercent(minValue, minAllowed, maxAllowed);
    const maxPercent = toPercent(maxValue, minAllowed, maxAllowed);

    const handleMinChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextMin = clamp(Math.round(Number(event.target.value)), minAllowed, maxValue);
        latestValuesRef.current = { min: nextMin, max: maxValue };
        onChange(nextMin, maxValue);
    };

    const handleMaxChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextMax = clamp(Math.round(Number(event.target.value)), minValue, maxAllowed);
        latestValuesRef.current = { min: minValue, max: nextMax };
        onChange(minValue, nextMax);
    };

    const handleCommit = () => {
        const { min, max } = latestValuesRef.current;
        onCommit(min, max);
    };

    return (
        <div className={className}>
            <div className="relative h-4 w-full">
                <div
                    className="absolute top-1/2 right-2 left-2 h-1 -translate-y-1/2 rounded-full bg-border"
                    aria-hidden="true"
                />
                <div
                    className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-foreground"
                    style={{
                        left: `calc(${THUMB_RADIUS} + (100% - ${THUMB_SIZE}) * ${minPercent} / 100)`,
                        width: `calc((100% - ${THUMB_SIZE}) * ${maxPercent - minPercent} / 100)`,
                    }}
                    aria-hidden="true"
                />
                <input
                    type="range"
                    min={minAllowed}
                    max={maxAllowed}
                    step={1}
                    value={minValue}
                    onChange={handleMinChange}
                    onPointerUp={handleCommit}
                    onKeyUp={handleCommit}
                    aria-label={minLabel}
                    aria-valuemin={minAllowed}
                    aria-valuemax={maxAllowed}
                    aria-valuenow={minValue}
                    className={cn(rangeInputClassName, 'z-10')}
                />
                <input
                    type="range"
                    min={minAllowed}
                    max={maxAllowed}
                    step={1}
                    value={maxValue}
                    onChange={handleMaxChange}
                    onPointerUp={handleCommit}
                    onKeyUp={handleCommit}
                    aria-label={maxLabel}
                    aria-valuemin={minAllowed}
                    aria-valuemax={maxAllowed}
                    aria-valuenow={maxValue}
                    className={cn(rangeInputClassName, 'z-20')}
                />
            </div>
        </div>
    );
}
